import { Component, OnInit, effect, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';

import { ToastModule } from 'primeng/toast';

import { MobileBootstrapService }      from './core/services/mobile-bootstrap.service';
import { BiometricAuthService }        from './core/services/biometric-auth.service';
import { MetaPixelService }            from './core/services/meta-pixel.service';
import { GoogleAdsService }            from './core/services/google-ads.service';
import { AppInsightsService }          from './core/services/app-insights.service';
import { ClarityService }              from './core/services/clarity.service';
import { ConsentService }              from './core/services/consent.service';
import { AuthService }                 from './core/services/auth.service';
import { BiometricLockComponent }      from './core/biometric-lock/biometric-lock.component';
import { ConsentBannerComponent }      from './core/consent/consent-banner.component';
import { ConciergeBannerComponent }    from './core/concierge/concierge-banner.component';
import { NotificationService }         from './core/notifications/notification.service';
import { NotificationsPanelComponent } from './core/notifications/notifications-panel.component';
import { MfaChallengeComponent }       from './core/mfa/mfa-challenge.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    ToastModule,
    BiometricLockComponent,
    ConsentBannerComponent,
    ConciergeBannerComponent,
    NotificationsPanelComponent,
    MfaChallengeComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private   readonly mobile        = inject(MobileBootstrapService);
  protected readonly biometric     = inject(BiometricAuthService);
  private   readonly auth          = inject(AuthService);
  private   readonly notifications = inject(NotificationService);
  private   readonly metaPixel     = inject(MetaPixelService);
  private   readonly googleAds     = inject(GoogleAdsService);
  private   readonly appInsights   = inject(AppInsightsService);
  private   readonly clarity       = inject(ClarityService);
  private   readonly consent       = inject(ConsentService);

  title = 'emitroll-app';

  /** Start polling + register for push when signed in; stop / unregister when signed out. */
  private readonly _watchAuth = effect(() => {
    if (this.auth.isLoggedIn()) {
      this.notifications.startPolling();
      void this.notifications.registerPushIfAvailable();
    } else {
      this.notifications.stopPolling();
    }
  });

  /** Load the marketing tags (Meta Pixel, Google Ads) only after the user grants
   *  marketing consent, and only in production where their ids are configured.
   *  Both init()s are idempotent, so they fire once when consent flips on. */
  private readonly _pixelOnConsent = effect(() => {
    if (this.consent.marketingGranted()) {
      this.metaPixel.init();
      this.googleAds.init();
    }
  });

  /** Load Application Insights + Clarity once Analytics consent is granted (and
   *  only where each is configured — App Insights needs a connection string,
   *  Clarity a project id, set in prod only). Both init()s are idempotent. */
  private readonly _analyticsOnConsent = effect(() => {
    if (this.consent.analyticsGranted()) {
      this.appInsights.init();
      this.clarity.init();
    }
  });

  /** Tag telemetry with the active employer id (opaque GUID) so funnels can be
   *  attributed per employer; cleared on sign-out. */
  private readonly _insightsUser = effect(() => {
    this.appInsights.setUser(this.auth.currentEmployerId());
  });

  ngOnInit(): void {
    void this.mobile.init();
  }
}
